import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import Logo from '../../../Images/logo.png';
import { Button } from '../../ui';
import { cx } from '../../ui/utils';
import { sidebarActions, useSidebarState } from '../sidebarStore';
import './AppSidebar.css';

const DESKTOP_QUERY = '(min-width: 1024px)';

const isDesktop = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DESKTOP_QUERY).matches
    : true;

// Faqat ko'rinish. Bandlar ro'yxati, faol guruh holati va log out funksiyasi
// rolga xos sidebar fayllaridan (MentorSidebar, AdminSidebar, SuperAdminSidebar) keladi.
//
// groups: [{
//   label,                      // guruh nomi
//   items: [{ to, label, icon, onClick? }],
//   collapsible?, open?, onToggle?, active?, icon?   // ochiladigan-yopiladigan guruh uchun
// }]
// Bandlari bo'sh guruh ko'rsatilmaydi.
function AppSidebar({ groups, onLogout, logoutLabel = 'Log out' }) {
  const { collapsed, mobileOpen } = useSidebarState();
  const { pathname } = useLocation();
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    sidebarActions.closeMobile();
  }, [pathname]);

  useEffect(() => {
    setTooltip(null);
  }, [collapsed]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') sidebarActions.closeMobile();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  // Yig'ilgan holatda (faqat desktop) ikonka ustiga kelganda nomi tooltip bo'lib chiqadi.
  // Tooltip position: fixed — nav'ning overflow'i uni kesib qo'ymasligi uchun.
  const tooltipProps = (label) => ({
    onMouseEnter: (event) => {
      if (!collapsed || !isDesktop()) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltip({ label, top: rect.top + rect.height / 2 });
    },
    onFocus: (event) => {
      if (!collapsed || !isDesktop()) return;
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltip({ label, top: rect.top + rect.height / 2 });
    },
    onMouseLeave: () => setTooltip(null),
    onBlur: () => setTooltip(null),
  });

  const renderItems = (items, nested) => (
    <ul className={cx('ui-sidebar__list', nested && 'ui-sidebar__list--nested')}>
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={`${item.label}-${item.to}`} className="ui-sidebar__item">
            <NavLink
              to={item.to}
              className={({ isActive }) => cx('ui-sidebar__link', isActive && 'ui-sidebar__link--active')}
              onClick={(event) => {
                if (item.onClick) item.onClick(event);
                sidebarActions.closeMobile();
              }}
              aria-label={collapsed ? item.label : undefined}
              {...tooltipProps(item.label)}
            >
              {Icon && <Icon className="ui-sidebar__icon" aria-hidden="true" />}
              <span className="ui-sidebar__label">{item.label}</span>
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  const visibleGroups = groups.filter((group) => group.items && group.items.length > 0);

  return (
    <>
      {mobileOpen && (
        <div className="ui-sidebar-overlay" onClick={sidebarActions.closeMobile} aria-hidden="true" />
      )}
      <aside
        className={cx(
          'ui-sidebar',
          collapsed && 'ui-sidebar--collapsed',
          mobileOpen && 'ui-sidebar--mobile-open'
        )}
        aria-label="Asosiy menyu"
      >
        <div className="ui-sidebar__brand">
          <span className="ui-sidebar__logo">
            <img src={Logo} alt="IIFA" className="ui-sidebar__logo-img" />
          </span>
          <button
            type="button"
            className="ui-sidebar__close"
            onClick={sidebarActions.closeMobile}
            aria-label="Menyuni yopish"
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <nav className="ui-sidebar__nav" onScroll={() => setTooltip(null)}>
          {visibleGroups.map((group) =>
            group.collapsible ? (
              <div key={group.label} className="ui-sidebar__group">
                <button
                  type="button"
                  className={cx(
                    'ui-sidebar__group-toggle',
                    group.active && 'ui-sidebar__group-toggle--active',
                    group.open && 'ui-sidebar__group-toggle--open'
                  )}
                  onClick={group.onToggle}
                  aria-expanded={Boolean(group.open)}
                  aria-label={collapsed ? group.label : undefined}
                  {...tooltipProps(group.label)}
                >
                  {group.icon && <group.icon className="ui-sidebar__group-icon" aria-hidden="true" />}
                  <span className="ui-sidebar__group-text">{group.label}</span>
                  <ChevronDown className="ui-sidebar__chevron" aria-hidden="true" />
                </button>
                {group.open && renderItems(group.items, true)}
              </div>
            ) : (
              <div key={group.label} className="ui-sidebar__group">
                <div className="ui-sidebar__group-label">
                  <span className="ui-sidebar__group-text">{group.label}</span>
                </div>
                {renderItems(group.items, false)}
              </div>
            )
          )}
        </nav>

        <div className="ui-sidebar__footer">
          <button
            type="button"
            className="ui-sidebar__link ui-sidebar__collapse"
            onClick={sidebarActions.toggleCollapsed}
            aria-label={collapsed ? 'Menyuni kengaytirish' : undefined}
            aria-pressed={collapsed}
            {...tooltipProps('Menyuni kengaytirish')}
          >
            {collapsed ? (
              <PanelLeftOpen className="ui-sidebar__icon" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="ui-sidebar__icon" aria-hidden="true" />
            )}
            <span className="ui-sidebar__label">Yig'ish</span>
          </button>
          <Button
            variant="ghost"
            fullWidth
            leftIcon={<LogOut />}
            onClick={onLogout}
            className="ui-sidebar__logout"
            aria-label={collapsed ? logoutLabel : undefined}
            {...tooltipProps(logoutLabel)}
          >
            {logoutLabel}
          </Button>
        </div>
      </aside>

      {tooltip && (
        <div className="ui-sidebar-tooltip" role="tooltip" style={{ top: tooltip.top }}>
          {tooltip.label}
        </div>
      )}
    </>
  );
}

export default AppSidebar;
